import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { AppDispatch } from "../../store/store";
import TransactionsDialog from "./TransactionsDialog";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCombinedRepeatsAndReferrers,
  getTransactionsByEntityId,
} from "../../store/modules/repeatsAndReferrers/repeatsAndReferrersActions";
import theme from "../../styles/theme";

type TransactionFilterType = "all" | "repeats" | "referrals";

interface IProps {
  isPro: boolean;
  coAgentIds: number[];
}

const ClientRepeatsAndReferrersTab: React.FC<IProps> = ({
  isPro,
  coAgentIds,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const combinedRepeatsAndReferrers = useSelector(
    (state: RootState) => state.repeatsAndReferrers.combinedRepeatsAndReferrers,
  );
  const combinedData = combinedRepeatsAndReferrers?.data || [];
  const loading = combinedRepeatsAndReferrers?.loading || false;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    id: number;
    name: string;
    filterType: TransactionFilterType;
  } | null>(null);

  useQuery({
    queryKey: ["fetchCombinedRepeatsAndReferrers", coAgentIds],
    queryFn: () => {
      dispatch(fetchCombinedRepeatsAndReferrers(coAgentIds));

      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  // Fetch transactions based on entity name and filter type
  const getContactTypeIdForFilter = (
    filterType: TransactionFilterType,
  ): number | null => {
    switch (filterType) {
      case "repeats":
        return 1; // Client repeats
      case "referrals":
        return 0; // Referrals (special backend value for referral types 3, 5, 6)
      case "all":
      default:
        return null;
    }
  };

  const { data: selectedTransactions = [], isLoading: transactionsLoading } =
    useQuery({
      queryKey: [
        "combinedTransactions",
        selectedEntity?.id,
        selectedEntity?.filterType,
        coAgentIds,
      ],
      queryFn: async () => {
        if (!selectedEntity?.id) return [];

        const contactTypeId = getContactTypeIdForFilter(
          selectedEntity.filterType,
        );
        const action = getTransactionsByEntityId(
          selectedEntity.id,
          contactTypeId,
        );
        const transactions = await action();

        return transactions;
      },
      enabled: !!selectedEntity?.id,
      refetchOnWindowFocus: false,
    });

  const handleViewTransactions = (
    entityId: number,
    entityName: string,
    filterType: TransactionFilterType = "all",
  ) => {
    setSelectedEntity({ id: entityId, name: entityName, filterType });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEntity(null);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"],
      flex: 2,
    },
    {
      field: "repeatCount",
      headerName: "Repeats",
      minWidth: isMobile ? 90 : 120,
      sortable: true,
      filterable: false,
      sortingOrder: ["desc", "asc"],
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleViewTransactions(params.row.id, params.row.name, "repeats");
          }}
          sx={{ cursor: "pointer" }}
        />
      ),
    },
    {
      field: "referralCount",
      headerName: "Referrals",
      minWidth: isMobile ? 90 : 120,
      sortable: true,
      filterable: false,
      sortingOrder: ["desc", "asc"],
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleViewTransactions(params.row.id, params.row.name, "referrals");
          }}
          sx={{ cursor: "pointer" }}
        />
      ),
    },
    {
      field: "count",
      headerName: "Total Units",
      minWidth: isMobile ? 100 : 130,
      sortable: true,
      filterable: false,
      sortingOrder: ["desc", "asc"],
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleViewTransactions(params.row.id, params.row.name, "all");
          }}
          sx={{ cursor: "pointer" }}
        />
      ),
    },
    {
      field: "totalVolume",
      headerName: "Total Volume",
      minWidth: 120,
      flex: 1,
      type: "number",
      filterable: false,
      sortable: true,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
    {
      field: "averagePrice",
      headerName: "Average Price",
      minWidth: 120,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
    {
      field: "totalGci",
      headerName: "Total GCI",
      minWidth: 120,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
    {
      field: "totalNetIncome",
      headerName: "Total Net Income",
      minWidth: 140,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ height: "100%", width: "100%" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : combinedData.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ py: 4 }}>
              No client repeats or referrers found.
            </Typography>
          ) : (
            <DataGrid
              rows={combinedData}
              columns={columns}
              getRowId={(row) => row.name}
              autoHeight
              onRowClick={(params) =>
                handleViewTransactions(params.row.id, params.row.name, "all")
              }
              density={isMobile ? "compact" : "standard"}
              disableColumnSelector
              disableColumnMenu={isMobile}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20 },
                },
                sorting: {
                  sortModel: [{ field: "count", sort: "desc" }],
                },
              }}
            />
          )}
        </Box>
      </Paper>

      {selectedEntity && (
        <TransactionsDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          transactions={selectedTransactions}
          title={`${
            selectedEntity.filterType === "repeats"
              ? "Repeat Transactions"
              : selectedEntity.filterType === "referrals"
                ? "Referral Transactions"
                : "All Transactions"
          } for ${selectedEntity.name}`}
          loading={transactionsLoading}
        />
      )}
    </Box>
  );
};

export default ClientRepeatsAndReferrersTab;
